// Vault Firestore service for CRUD operations

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  VaultItem,
  VaultFolder,
  CreateVaultItem,
  CreateVaultFolder,
  EncryptedBlob,
} from '../models/vault.model';
import {
  encryptWithPassphrase,
  decryptWithPassphrase,
  isVaultUnlocked,
} from './vault-crypto.service';

const VAULT_ITEMS_COLLECTION = 'vaultItems';
const VAULT_FOLDERS_COLLECTION = 'vaultFolders';

// Convert Firestore timestamp to Date
function timestampToDate(timestamp: Timestamp | Date): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return timestamp;
}

// ============ VAULT ITEMS ============

export async function createVaultItem(
  item: Omit<CreateVaultItem, 'encryptedPassword'> & { password: string }
): Promise<string> {
  if (!isVaultUnlocked()) {
    throw new Error('Vault is locked');
  }

  const encryptedBlob = await encryptWithPassphrase(item.password);

  const docRef = await addDoc(collection(db, VAULT_ITEMS_COLLECTION), {
    title: item.title,
    username: item.username || null,
    encryptedPassword: JSON.stringify(encryptedBlob),
    url: item.url || null,
    notes: item.notes || null,
    folderId: item.folderId || null,
    isTeam: item.isTeam,
    createdBy: item.createdBy,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  return docRef.id;
}

export async function updateVaultItem(
  id: string,
  updates: Partial<Omit<VaultItem, 'id' | 'createdAt' | 'createdBy'>> & { password?: string }
): Promise<void> {
  if (!isVaultUnlocked()) {
    throw new Error('Vault is locked');
  }

  const updateData: Record<string, unknown> = {
    updatedAt: Timestamp.now(),
  };

  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.username !== undefined) updateData.username = updates.username;
  if (updates.url !== undefined) updateData.url = updates.url;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.folderId !== undefined) updateData.folderId = updates.folderId;
  if (updates.isTeam !== undefined) updateData.isTeam = updates.isTeam;

  if (updates.password !== undefined) {
    const encryptedBlob = await encryptWithPassphrase(updates.password);
    updateData.encryptedPassword = JSON.stringify(encryptedBlob);
  }

  await updateDoc(doc(db, VAULT_ITEMS_COLLECTION, id), updateData);
}

export async function deleteVaultItem(id: string): Promise<void> {
  await deleteDoc(doc(db, VAULT_ITEMS_COLLECTION, id));
}

export async function decryptVaultItemPassword(item: VaultItem): Promise<string> {
  if (!isVaultUnlocked()) {
    throw new Error('Vault is locked');
  }

  const blob: EncryptedBlob = JSON.parse(item.encryptedPassword);
  return decryptWithPassphrase(blob);
}

// Subscribe to vault items for a user
export function subscribeToVaultItems(
  userId: string,
  includeTeam: boolean,
  callback: (items: VaultItem[]) => void
): () => void {
  // Query for user's personal items OR team items
  const personalQuery = query(
    collection(db, VAULT_ITEMS_COLLECTION),
    where('createdBy', '==', userId),
    where('isTeam', '==', false),
    orderBy('updatedAt', 'desc')
  );

  const teamQuery = query(
    collection(db, VAULT_ITEMS_COLLECTION),
    where('isTeam', '==', true),
    orderBy('updatedAt', 'desc')
  );

  const items: Map<string, VaultItem> = new Map();
  let personalLoaded = false;
  let teamLoaded = !includeTeam;

  const notifyIfReady = () => {
    if (personalLoaded && teamLoaded) {
      const allItems = Array.from(items.values()).sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
      );
      callback(allItems);
    }
  };

  const unsubPersonal = onSnapshot(personalQuery, (snapshot) => {
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      items.set(doc.id, {
        id: doc.id,
        title: data.title,
        username: data.username,
        encryptedPassword: data.encryptedPassword,
        url: data.url,
        notes: data.notes,
        folderId: data.folderId,
        isTeam: data.isTeam,
        createdBy: data.createdBy,
        createdAt: timestampToDate(data.createdAt),
        updatedAt: timestampToDate(data.updatedAt),
      });
    });
    // Handle deletions
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'removed') {
        items.delete(change.doc.id);
      }
    });
    personalLoaded = true;
    notifyIfReady();
  });

  let unsubTeam: (() => void) | null = null;
  if (includeTeam) {
    unsubTeam = onSnapshot(teamQuery, (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        items.set(doc.id, {
          id: doc.id,
          title: data.title,
          username: data.username,
          encryptedPassword: data.encryptedPassword,
          url: data.url,
          notes: data.notes,
          folderId: data.folderId,
          isTeam: data.isTeam,
          createdBy: data.createdBy,
          createdAt: timestampToDate(data.createdAt),
          updatedAt: timestampToDate(data.updatedAt),
        });
      });
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          items.delete(change.doc.id);
        }
      });
      teamLoaded = true;
      notifyIfReady();
    });
  }

  return () => {
    unsubPersonal();
    if (unsubTeam) unsubTeam();
  };
}

// ============ VAULT FOLDERS ============

export async function createVaultFolder(folder: CreateVaultFolder): Promise<string> {
  const docRef = await addDoc(collection(db, VAULT_FOLDERS_COLLECTION), {
    name: folder.name,
    isTeam: folder.isTeam,
    createdBy: folder.createdBy,
    createdAt: Timestamp.now(),
  });

  return docRef.id;
}

export async function updateVaultFolder(
  id: string,
  updates: Partial<Omit<VaultFolder, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  await updateDoc(doc(db, VAULT_FOLDERS_COLLECTION, id), updates);
}

export async function deleteVaultFolder(id: string): Promise<void> {
  // First, update all items in this folder to have no folder
  const itemsQuery = query(
    collection(db, VAULT_ITEMS_COLLECTION),
    where('folderId', '==', id)
  );
  const snapshot = await getDocs(itemsQuery);

  const updatePromises = snapshot.docs.map((doc) =>
    updateDoc(doc.ref, { folderId: null })
  );
  await Promise.all(updatePromises);

  // Then delete the folder
  await deleteDoc(doc(db, VAULT_FOLDERS_COLLECTION, id));
}

// Subscribe to vault folders for a user
export function subscribeToVaultFolders(
  userId: string,
  includeTeam: boolean,
  callback: (folders: VaultFolder[]) => void
): () => void {
  const personalQuery = query(
    collection(db, VAULT_FOLDERS_COLLECTION),
    where('createdBy', '==', userId),
    where('isTeam', '==', false),
    orderBy('name', 'asc')
  );

  const teamQuery = query(
    collection(db, VAULT_FOLDERS_COLLECTION),
    where('isTeam', '==', true),
    orderBy('name', 'asc')
  );

  const folders: Map<string, VaultFolder> = new Map();
  let personalLoaded = false;
  let teamLoaded = !includeTeam;

  const notifyIfReady = () => {
    if (personalLoaded && teamLoaded) {
      const allFolders = Array.from(folders.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      callback(allFolders);
    }
  };

  const unsubPersonal = onSnapshot(personalQuery, (snapshot) => {
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      folders.set(doc.id, {
        id: doc.id,
        name: data.name,
        isTeam: data.isTeam,
        createdBy: data.createdBy,
        createdAt: timestampToDate(data.createdAt),
      });
    });
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'removed') {
        folders.delete(change.doc.id);
      }
    });
    personalLoaded = true;
    notifyIfReady();
  });

  let unsubTeam: (() => void) | null = null;
  if (includeTeam) {
    unsubTeam = onSnapshot(teamQuery, (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        folders.set(doc.id, {
          id: doc.id,
          name: data.name,
          isTeam: data.isTeam,
          createdBy: data.createdBy,
          createdAt: timestampToDate(data.createdAt),
        });
      });
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          folders.delete(change.doc.id);
        }
      });
      teamLoaded = true;
      notifyIfReady();
    });
  }

  return () => {
    unsubPersonal();
    if (unsubTeam) unsubTeam();
  };
}
